import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, Navigation, Clapperboard, SlidersHorizontal, Quote } from "lucide-react";

const A = "https://customer-assets-m6fa6gv7.emergentagent.net/job_filmmakerakki/artifacts";
const C = "https://thumb.wikimedia.org/wikipedia/commons/thumb";
const ease = [0.16, 1, 0.3, 1];

// Real on-set moments — auto-sliding
const BTS_SLIDES = [
  { img: "/assets/bts-1.jpg", caption: "On set — Your Honor Season 2 (Sony LIV)" },
  { img: "/assets/bts-2.jpg", caption: "With director Anubhav Sinha" },
  { img: "/assets/bts-7.jpg", caption: "With Rajkummar Rao — on set" },
  { img: "/assets/bts-8.jpg", caption: "With Govind Namdev ji" },
  { img: "/assets/bts-3.jpg", caption: "With Manav Vij — Your Honor set" },
  { img: "/assets/bts-6.jpg", caption: "Camera unit — feature film set" },
  { img: "/assets/bts-9.jpg", caption: "Pre-flight walkthrough — drone unit" },
  { img: "/assets/bts-4.jpg", caption: "Documentary unit — Varanasi Ghats" },
  { img: "/assets/bts-5.jpg", caption: "Night schedule — on location" },
  { img: "/assets/bts-10.jpg", caption: "Wrap-up — event coverage" },
];

const FILM_STRIP = [
  { img: `${A}/a42pyai9_Screenshot%202026-09-01%20at%202.57.24%E2%80%AFAM.png`, caption: "Drone Setup" },
  { img: `${A}/nzaw9cbo_Screenshot%202026-09-01%20at%202.57.42%E2%80%AFAM.png`, caption: "Camera Rig" },
  { img: `${A}/5c06c8xr_Screenshot%202026-09-01%20at%202.59.16%E2%80%AFAM.png`, caption: "On-Set Preparation" },
  { img: `${C}/9/9b/Begnas_Lake%2C_Pokhara%2C_Kaski.jpg/960px-Begnas_Lake%2C_Pokhara%2C_Kaski.jpg`, caption: "Landscape Scouting" },
  { img: `${A}/u2ld2p1z_Screenshot%202026-09-01%20at%203.01.06%E2%80%AFAM.png`, caption: "Drone Unit — With Cast" },
  { img: "/assets/hero-main.jpg", caption: "Flight Planning" },
];

const WORKFLOW = [
  { n: "01", title: "Scout", desc: "Location planning", icon: MapPin },
  { n: "02", title: "Fly", desc: "Precision aerial capture", icon: Navigation },
  { n: "03", title: "Shoot", desc: "Cinematic storytelling", icon: Clapperboard },
  { n: "04", title: "Finish", desc: "Professional editing & color", icon: SlidersHorizontal },
];

// Film-strip perforation strips (top + bottom punched holes)
const Perforation = () => (
  <div
    aria-hidden
    className="h-5 bg-[#0F2A43]"
    style={{
      backgroundImage: "radial-gradient(circle, #F8F6F2 2.5px, transparent 3px)",
      backgroundSize: "26px 100%",
      backgroundPosition: "center",
    }}
  />
);

export const BtsShowcase = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % BTS_SLIDES.length), 4000);
    return () => clearInterval(t);
  }, []);

  return (
  <section id="bts" data-testid="bts-section" className="bg-[#F8F6F2] py-[100px] md:py-[120px] border-t border-[#E5E7EB] overflow-hidden">
    <div className="max-w-[1320px] mx-auto px-6 md:px-10">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease }}
        className="max-w-2xl"
      >
        <p className="font-mono text-[12px] tracking-[0.35em] uppercase text-[#4F6D8A]">Behind The Scenes</p>
        <h2 className="mt-4 font-serif text-[34px] md:text-[48px] xl:text-[56px] leading-[1.05] text-[#111827]">Every Frame Has a Story</h2>
        <motion.span
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.25, ease }}
          className="mt-5 block w-14 h-[2px] bg-[#4F6D8A] origin-left"
        />
        <p className="mt-5 text-[#6B7280] leading-relaxed">
          From mountain locations to Bollywood sets, every project begins long before the camera starts rolling.
        </p>
      </motion.div>

      {/* Asymmetric editorial: hero image + workflow stacked */}
      <div className="mt-14 grid lg:grid-cols-12 gap-7 items-start">
        <motion.figure
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7, ease }}
          className="lg:col-span-7 group"
          data-testid="bts-hero"
        >
          <div className="relative rounded-[22px] overflow-hidden border border-[#E5E7EB] shadow-[0_20px_60px_-20px_rgba(15,42,67,0.3)] aspect-[16/10] bg-[#0F2A43]/5">
            <AnimatePresence mode="wait">
              <motion.img
                key={idx}
                src={BTS_SLIDES[idx].img}
                alt={BTS_SLIDES[idx].caption}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease }}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </AnimatePresence>
            <span className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/35 backdrop-blur px-3.5 py-2 rounded-full">
              {BTS_SLIDES.map((_, i) => (
                <button
                  key={i}
                  data-testid={`bts-dot-${i}`}
                  onClick={() => setIdx(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${i === idx ? "bg-white w-4" : "bg-white/50"}`}
                />
              ))}
            </span>
          </div>
          <figcaption className="mt-4 font-mono text-[10px] tracking-[0.2em] uppercase text-[#6B7280] min-h-[14px]">
            <AnimatePresence mode="wait">
              <motion.span
                key={idx}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="block"
              >
                — {BTS_SLIDES[idx].caption}
              </motion.span>
            </AnimatePresence>
          </figcaption>
        </motion.figure>

        {/* Workflow */}
        <div className="lg:col-span-5 grid grid-cols-2 gap-5">
          {WORKFLOW.map((w, i) => {
            const Icon = w.icon;
            return (
              <motion.div
                key={w.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: i * 0.08, ease }}
                data-testid={`bts-workflow-${i}`}
                className="bg-white rounded-[18px] border border-[#E5E7EB] p-6 shadow-[0_10px_30px_-15px_rgba(15,42,67,0.15)] hover:-translate-y-1.5 hover:shadow-[0_25px_50px_-20px_rgba(15,42,67,0.35)] transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <Icon size={20} strokeWidth={1.3} className="text-[#4F6D8A]" />
                  <span className="font-serif text-2xl text-[#4F6D8A]/40">{w.n}</span>
                </div>
                <p className="mt-5 font-display font-bold text-lg text-[#111827]">{w.title}</p>
                <p className="mt-1 font-mono text-[8px] tracking-[0.2em] uppercase text-[#6B7280]">{w.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Film strip gallery */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.7, ease }}
        className="mt-16 rounded-[22px] overflow-hidden shadow-[0_25px_60px_-20px_rgba(15,42,67,0.35)]"
        data-testid="bts-filmstrip"
      >
        <Perforation />
        <div className="bg-[#0F2A43] py-5">
          <div className="flex gap-5 overflow-x-auto px-5 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {FILM_STRIP.map((f, i) => (
              <figure key={f.caption} className="group shrink-0 w-[220px] md:w-[260px] snap-start" data-testid={`bts-frame-${i}`}>
                <div className="rounded-[14px] overflow-hidden border border-white/10">
                  <img
                    src={f.img}
                    alt={f.caption}
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <figcaption className="mt-3 font-mono text-[8px] tracking-[0.25em] uppercase text-white/60 group-hover:text-white transition-colors">
                  {String(i + 1).padStart(2, "0")} — {f.caption}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
        <Perforation />
      </motion.div>

      {/* Signature quote */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease }}
        className="mt-16"
        data-testid="bts-quote"
      >
        <div className="bg-white rounded-[22px] border border-[#E5E7EB] shadow-[0_30px_70px_-25px_rgba(15,42,67,0.3)] px-8 py-14 md:py-16 text-center max-w-4xl mx-auto">
          <Quote size={30} className="mx-auto text-[#4F6D8A]/50" fill="currentColor" strokeWidth={0} />
          <p className="mt-6 font-serif italic text-2xl md:text-4xl text-[#111827] leading-snug max-w-3xl mx-auto">
            "The best cinematic moments happen before the audience ever sees the first frame."
          </p>
          <span className="mt-7 mx-auto block w-12 h-[2px] bg-[#4F6D8A]" />
        </div>
      </motion.div>
    </div>
  </section>
  );
};
